import { z } from 'zod';
import { ErrEnvelopeSchema, okEnvelopeSchema, type ErrorCode } from '../../src/shared/http';

/**
 * Typed SPA client core for the JSON API (ARCHITECTURE_PLAN §9). Every endpoint
 * speaks the shared envelope — `{ ok: true, data } | { ok: false, error: { code,
 * message, requestId } }` — defined in `src/shared/http.ts` (the single source of
 * truth, §3). Responses are validated against the shared zod schemas here so a
 * contract drift between Worker and SPA fails loudly instead of silently
 * rendering garbage.
 *
 * Failure model (no blank failure states, §10):
 *   - `ApiRequestError`  — the Worker returned the §9 error envelope. Carries the
 *     SAFE message, the closed-union `code`, the HTTP status, and the `requestId`
 *     the UI must render.
 *   - `ApiTransportError` — network failure, non-JSON body, or an envelope that
 *     does not match the contract. Carries the `x-request-id` response header
 *     when one was seen (`null` when the request never reached the Worker).
 */

/** The Worker returned a well-formed §9 error envelope. */
export class ApiRequestError extends Error {
  public override readonly name = 'ApiRequestError';
  /** Closed §9 error-code union — safe for client-side branching. */
  readonly code: ErrorCode;
  readonly httpStatus: number;
  /** Correlates with Worker logs; always rendered to the user (§9, §11). */
  readonly requestId: string;

  constructor(message: string, code: ErrorCode, httpStatus: number, requestId: string) {
    super(message);
    this.code = code;
    this.httpStatus = httpStatus;
    this.requestId = requestId;
  }
}

/** The request never produced a valid envelope (transport or contract failure). */
export class ApiTransportError extends Error {
  public override readonly name = 'ApiTransportError';
  /** From the `x-request-id` response header; `null` when nothing was received. */
  readonly requestId: string | null;

  constructor(message: string, requestId: string | null, options?: { cause?: unknown }) {
    super(message, options?.cause !== undefined ? { cause: options.cause } : undefined);
    this.requestId = requestId;
  }
}

/**
 * Core request helper. Sends/receives JSON, validates the envelope, and either
 * returns the parsed `data` payload or throws one of the typed errors above.
 * The session cookie rides along automatically (same-origin fetch, §6.1) and
 * state-changing requests set `Content-Type: application/json` as the CSRF
 * guard requires (§6.2).
 */
async function request<T>(
  method: string,
  path: string,
  body: unknown,
  dataSchema: z.ZodType<T>,
  signal?: AbortSignal
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: {
        accept: 'application/json',
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal !== undefined ? { signal } : {}),
      credentials: 'same-origin',
    });
  } catch (cause) {
    throw new ApiTransportError(
      `Could not reach the API (${method} ${path}). Check your connection and try again.`,
      null,
      { cause }
    );
  }

  const requestId = response.headers.get('x-request-id');

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch (cause) {
    throw new ApiTransportError(
      `The API returned a malformed response (HTTP ${String(response.status)}).`,
      requestId,
      { cause }
    );
  }

  // A well-formed error envelope always carries the safe message + requestId.
  const errParsed = ErrEnvelopeSchema.safeParse(parsed);
  if (errParsed.success) {
    throw new ApiRequestError(
      errParsed.data.error.message,
      errParsed.data.error.code,
      response.status,
      errParsed.data.error.requestId
    );
  }

  if (!response.ok) {
    throw new ApiTransportError(
      `The API returned HTTP ${String(response.status)} with an unexpected body.`,
      requestId
    );
  }

  const okParsed = okEnvelopeSchema(dataSchema).safeParse(parsed);
  if (!okParsed.success) {
    throw new ApiTransportError(
      'The API response did not match the expected contract.',
      requestId,
      {
        cause: okParsed.error,
      }
    );
  }
  return okParsed.data.data;
}

/** GET helper with response validation. */
export function apiGet<T>(
  path: string,
  dataSchema: z.ZodType<T>,
  signal?: AbortSignal
): Promise<T> {
  return request('GET', path, undefined, dataSchema, signal);
}

/** POST helper with a JSON body and response validation. */
export function apiPost<T>(path: string, body: unknown, dataSchema: z.ZodType<T>): Promise<T> {
  return request('POST', path, body, dataSchema);
}

/** PATCH helper with a JSON body and response validation. */
export function apiPatch<T>(path: string, body: unknown, dataSchema: z.ZodType<T>): Promise<T> {
  return request('PATCH', path, body, dataSchema);
}

/** PUT helper with a JSON body and response validation. */
export function apiPut<T>(path: string, body: unknown, dataSchema: z.ZodType<T>): Promise<T> {
  return request('PUT', path, body, dataSchema);
}

/** DELETE helper with response validation (empty JSON body for the CSRF guard). */
export function apiDelete<T>(path: string, dataSchema: z.ZodType<T>): Promise<T> {
  return request('DELETE', path, {}, dataSchema);
}
